import { delay } from 'redux-saga';
import { call, put, fork, take, select } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';

import * as schemas from 'schemas';
import {
  getChatLastMessageId,
  getChatMessagesBuffer,
  getAuth,
  getChatItems,
  getCommentLiveListLastApprovedTime,
  getForumStatus
} from 'selectors';
import * as actions from 'actions';
import Socket, { CONNECTION_STATE_EVENTS } from 'socket';
import { httpRequestSagaMaker, navigateToWebsite } from 'utils/sagas';
import { isTokenValid, getAuthData } from 'authStore';
import { successActionFrom, errorActionFrom } from 'utils/actions';
import authBackgroundSaga from './auth/sagas';
import commentsBackgroundSaga from './comments/sagas';
import mailBackgroundSaga, { refreshMail } from './mail/sagas';
import magazineBackgroundSaga from './magazine/sagas';
import userBackgroundSaga from './user/sagas';
import journalBackgroundSaga from './journal/sagas';
import topicsBackgroundSaga from './topics/sagas';
import feedbackBackgroundSaga from './feedback/sagas';
import goalsBackgroundSaga from './goals/sagas';
import categoriesBackgroundSaga from './categories/sagas';
import f2fBackgroundSaga from './f2f/sagas';
import linksBackgroundSaga from './links/sagas';
import feedBackgroundSaga from './feed/sagas';
import searchBackgroundSaga from './search/sagas';
import infoBackgroundSaga from './info/sagas';
import chatBackgroundSaga, {
  filterUpdatedChatMessages,
  manageChatActiveStateUpdate,
  refreshChatQueueRejectionExpiration
} from './chat/sagas';
import analyticsBackgroundSaga from './analytics/sagas';
import analyticsTracker from './analytics/analyticsTracker';
import forumBackgroundSaga from './forum/sagas';

function* watchForErrors() {
  while(true) { // eslint-disable-line no-constant-condition
    const action = yield take('*');

    if(!action.error) {
      continue;
    }

    if(action.error.showInInfoBar === false) {
      continue;
    }

    if(action.type.startsWith('redux-form')) {
      continue;
    }

    if(action.causedByURLChange) {
      const { httpResponse } = action.error;

      if(httpResponse && httpResponse.status === 404) {
        yield put(push('/404'));
        continue;
      }
    }

    yield put(actions.infoShow('errors', action.errorMessage));
  }
}


export function* manageDataEvent(event, socket) {
  if(event.data.path === 'chat.state') {
    yield put(actions.chatStateUpdate(event.data.body));

    if(event.data.body.ban) {
      yield call(refreshChatQueueRejectionExpiration, event.data.body.ban);
    }
  }
  else if(event.data.path === 'mail.state') {
    yield call(refreshMail);
  }
  else if(event.data.path === 'forum.state') {
    yield put(actions.forumStatusUpdate(event.data.body));

    if(event.data.body.watching) {
      const forumStatus = yield select(getForumStatus);

      // This happen if the user closes the browser and then open it while the session is still running.
      // The server will think they disconnected and are still watching the thread.
      if(!forumStatus.localWatchedThreadId) {
        yield put(actions.forumThreadUnwatch(forumStatus.remoteWatchedThreadId));
      }
    }
  }
  else if(event.data.path === 'chat.new-messages') {
    const chatMessagesBuffer = yield select(getChatMessagesBuffer);
    const dataForChatMessagesNotInBuffer = filterUpdatedChatMessages(event.data.body, chatMessagesBuffer);

    if(dataForChatMessagesNotInBuffer.chatMessagesIds.length > 0) {
      yield put(actions.chatMessagesUpdate(dataForChatMessagesNotInBuffer));
    }
  }
  else if(event.data.path === 'chat.active-state') {
    yield put(actions.chatActiveStateUpdate(event.data.body));

    const panel = event.data.body.panel;

    if(panel) {
      const chatItems = yield select(getChatItems);
      const action = yield call(manageChatActiveStateUpdate, panel, chatItems);

      if(action) {
        yield put(action);
      }
    }
  }
  else if(event.data.path === 'chat.get-last-seen-message-id') {
    const lastChatMessageId = yield select(getChatLastMessageId);
    yield call([socket, socket.respond], event.data.id, lastChatMessageId);
  }
  else if(event.data.path === 'forum.get-last-seen-message-time') {
    const lastLiveCommentApprovedTime = yield select(getCommentLiveListLastApprovedTime);
    yield call([socket, socket.respond], event.data.id, lastLiveCommentApprovedTime);
  }
  else if(event.data.path === 'chat.eos') {
    const questionnaire = event.data.body;
    const { result, entities } = normalize(questionnaire, schemas.chatQuestionnaireSchema);
    yield put(actions.chatQuestionnairesReceived({ chatQuestionnairesIds: result, entities }));
  }
  else if(event.data.path === 'forum.new-messages') {
    const comments = event.data.body.messages;
    const { result, entities } = normalize(comments, schemas.commentsArraySchema);

    if(comments.length >= process.env.KOOTH_LIVE_COMMENTS_RECONNECT_COUNT) { // There's a high change we've been disconnected for too long, so we reset.
      yield put(actions.commentsResetLiveList());
    }

    yield put(actions.commentsReceived({ commentsIds: result, entities }, true));
  }
}

export function* manageConnectionStateEvents(event) {
  if(event.type === 'open') {
    yield put(actions.infoClose('connectionState'));
  }
  else {
    yield put(actions.infoShow('connectionState', event.statusMessage));
  }
}

export function* watchForEventsFromServer(socket) {
  const chan = yield call([socket, socket.getEventChannel]);

  while(true) { // eslint-disable-line no-constant-condition
    const event = yield take(chan);

    if(event.type === 'data') {
      yield fork(manageDataEvent, event, socket);
    }
    else if(CONNECTION_STATE_EVENTS.indexOf(event.type) >= 0) {
      const auth = yield select(getAuth);

      if(isTokenValid(auth)) {
        yield call(manageConnectionStateEvents, event);
      }
      else {
        yield call(navigateToWebsite);
      }
    }
  }
}

export function* refreshListsItems() {
  while(true) { // eslint-disable-line no-constant-condition
    yield call(delay, process.env.KOOTH_CHAT_MESSAGES_DATES_REFRESH_INTERNAL);
    yield put(actions.timeRefresh());
  }
}

function* watchForCreatedArticlesOrForumThreads() {
  while(true) { // eslint-disable-line no-constant-condition
    yield take([actions.MAGAZINE_ARTICLE_CREATE_SUCCESS, actions.FORUM_THREAD_CREATE_SUCCESS]);
    yield call([window, window.scrollTo], 0, 0);
  }
}

export default function* backgroundSaga() {
  const socket = new Socket(getAuthData().accessToken);
  const request = httpRequestSagaMaker(successActionFrom, errorActionFrom);
  const boundedFork = saga => fork(saga, socket, request);

  yield [
    boundedFork(authBackgroundSaga),
    boundedFork(commentsBackgroundSaga),
    boundedFork(mailBackgroundSaga),
    boundedFork(magazineBackgroundSaga),
    boundedFork(userBackgroundSaga),
    boundedFork(journalBackgroundSaga),
    boundedFork(topicsBackgroundSaga),
    boundedFork(feedbackBackgroundSaga),
    boundedFork(chatBackgroundSaga),
    boundedFork(goalsBackgroundSaga),
    boundedFork(categoriesBackgroundSaga),
    boundedFork(f2fBackgroundSaga),
    boundedFork(feedBackgroundSaga),
    boundedFork(searchBackgroundSaga),
    boundedFork(linksBackgroundSaga),
    boundedFork(forumBackgroundSaga),
    boundedFork(infoBackgroundSaga),
    fork(watchForCreatedArticlesOrForumThreads),
    fork(refreshListsItems),
    fork(watchForEventsFromServer, socket),
    fork(watchForErrors),
    fork(analyticsBackgroundSaga, analyticsTracker)
  ];
}



/** WEBPACK FOOTER **
 ** ./src/members/state/sagas.js
 **/